import { createTheme } from '@mui/material/styles';

/**
 * AsterLogic brand tokens, taken from the existing AsterGuard products so the
 * three sit together as one family.
 */
export const brand = {
  /** The gradient shared across every AsterLogic product. */
  gradient: 'linear-gradient(135deg, #2563EB, #7C3AED)',
  blue: '#3B82F6',
  blueDark: '#2563EB',
  violet: '#7C3AED',
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    /** One step above `background.paper`, for nested cards and table headers. */
    surfaceRaised: string;
    /** The brand gradient, for the logo lockup and primary calls to action. */
    gradient: string;
  }

  interface PaletteOptions {
    surfaceRaised?: string;
    gradient?: string;
  }
}

/**
 * The application theme.
 *
 * Uses MUI's CSS-VARIABLES mode rather than swapping a `ThemeProvider` value.
 * The difference is visible: with a provider swap the first paint always uses the
 * default scheme and then repaints, so every reload flashes the wrong colours for
 * a frame. CSS variables plus `InitColorSchemeScript` resolve the scheme before
 * React renders at all.
 */
export const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
  },

  colorSchemes: {
    light: {
      palette: {
        primary: { main: brand.blueDark },
        secondary: { main: brand.violet },
        background: { default: '#F7F8FA', paper: '#FFFFFF' },
        divider: '#E3E7ED',
        surfaceRaised: '#F2F4F7',
        gradient: brand.gradient,
        text: { primary: '#111827', secondary: '#4B5563' },
      },
    },

    dark: {
      palette: {
        primary: { main: brand.blue },
        secondary: { main: brand.violet },
        background: { default: '#0F1117', paper: '#141922' },
        divider: '#2A3347',
        surfaceRaised: '#1C2333',
        gradient: brand.gradient,
        text: { primary: '#E2E8F0', secondary: '#94A3B8' },
      },
    },
  },

  typography: {
    // Self-hosted through @fontsource, not a Google Fonts link. That keeps the
    // app working on a locked-down corporate network and avoids a third-party
    // request on every page load.
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',

    // Shouty uppercase buttons read as noise in a dense data application.
    button: { textTransform: 'none', fontWeight: 600 },

    h1: { fontSize: '1.75rem', fontWeight: 700 },
    h2: { fontSize: '1.5rem', fontWeight: 700 },
    h3: { fontSize: '1.25rem', fontWeight: 600 },
    h4: { fontSize: '1.125rem', fontWeight: 600 },
    h5: { fontSize: '1rem', fontWeight: 600 },
    h6: { fontSize: '0.9375rem', fontWeight: 600 },
  },

  shape: { borderRadius: 10 },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 10 } },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          // A 1px border rather than a shadow. Shadows are essentially invisible
          // against #0F1117, so a shadow-only card has no edge at all in dark
          // mode — the panel just dissolves into the page.
          border: `1px solid ${t.palette.divider}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        // MUI lightens elevated dark surfaces with a gradient overlay, which
        // fights the palette we defined. Turned off so surfaces stay the colours
        // chosen above.
        root: { backgroundImage: 'none' },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },

    MuiSelect: { defaultProps: { size: 'small' } },

    MuiTooltip: {
      defaultProps: { arrow: true },
    },

    MuiTableCell: {
      styleOverrides: {
        root: ({ theme: t }) => ({ borderColor: t.palette.divider }),
      },
    },

    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
});
